/**
 * P0 Sprint — Factuur bewerken dialoog.
 *
 * - Bewerkbare velden: klant + bedrijfsgegevens, adres, BTW/KvK,
 *   referenties, vervaldatum, factuurregels (toevoegen/wijzigen/verwijderen),
 *   omschrijving/aantal/prijs/BTW per regel, klant- en interne notitie.
 *
 * - Factuurnummer en id blijven onveranderd; revision wordt door backend bumped.
 *
 * - Voor (deels) betaalde factuur:
 *   - Vereist Manager/Admin/Owner/Webshop manager (orders:convert_financial).
 *   - Vereist verplichte `edit_reason`.
 *   - Backend blokkeert totaalverlaging onder reeds betaald bedrag (409).
 *
 * Backend: PATCH /api/admin/sales-invoices/{id}
 */
import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Edit3, Plus, Trash2, Loader2, CheckCircle2, AlertTriangle,
} from "lucide-react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../ui/select";
import { adminApi, useAuth } from "../../../context/AuthContext";
import InvoiceCustomerLinkPanel from "./InvoiceCustomerLinkPanel";
import { SUPPORTED_CURRENCIES } from "../../../lib/currencies";

function fmtEUR(value, currency = "EUR") {
  const num = Number(value || 0);
  return `${currency} ${num.toFixed(2).replace(".", ",")}`;
}

function normalizeLines(rawLines) {
  // P1 Bugfix (Feb 2026): quantity=0 is een VALIDE gebruikerswaarde en mag
  // niet stilzwijgend naar 1 worden verhoogd. Alleen null/undefined defaulten
  // naar 1 (nieuwe regel). Zie ook backend `sales_invoice_service.py`.
  return (rawLines || []).map((l) => ({
    description: l.description ?? "",
    quantity: l.quantity == null ? 1 : Number(l.quantity),
    unit: l.unit ?? "stuk",
    unit_price: l.unit_price ?? 0,
    vat_percent: l.vat_percent ?? 21,
  }));
}

export default function InvoiceEditDialog({ invoice, meta, onClose, onSaved }) {
  const { user, hasPermission } = useAuth();
  const userRole = (user?.role || "").toLowerCase();
  const canManageLink =
    hasPermission("orders:convert_financial") ||
    ["owner", "admin", "manager"].includes(userRole);

  const isProtected = invoice.status === "betaald" || invoice.status === "deels_betaald";
  const totalPaidExisting = Number(invoice.total_paid || 0);

  const [form, setForm] = useState({
    customer_name: invoice.customer_name || "",
    customer_company: invoice.customer_company || "",
    customer_email: invoice.customer_email || "",
    customer_phone: invoice.customer_phone || "",
    customer_whatsapp: invoice.customer_whatsapp || "",
    street: invoice.street || "",
    house_number: invoice.house_number || "",
    house_number_addition: invoice.house_number_addition || "",
    postal_code: invoice.postal_code || "",
    city: invoice.city || "",
    country: invoice.country || "Nederland",
    vat_number: invoice.vat_number || "",
    kvk_number: invoice.kvk_number || "",
    invoice_date: (invoice.invoice_date || "").slice(0, 10),
    due_date: (invoice.due_date || "").slice(0, 10),
    currency: invoice.currency || "EUR",
    payment_reference: invoice.payment_reference || "",
    customer_reference: invoice.customer_reference || "",
    service_type: invoice.service_type || "",
    destination: invoice.destination || "",
    customer_note: invoice.customer_note || "",
    internal_note: invoice.internal_note || "",
  });
  const [lines, setLines] = useState(normalizeLines(invoice.lines || []));
  const [editReason, setEditReason] = useState("");
  const [busy, setBusy] = useState(false);

  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const addLine = () => setLines((s) => [...s, { description: "", quantity: 1, unit: "stuk", unit_price: 0, vat_percent: 21 }]);
  const removeLine = (i) => setLines((s) => (s.length <= 1 ? s : s.filter((_, idx) => idx !== i)));
  const updateLine = (i, patch) => setLines((s) => s.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const applyPreset = (i, preset) =>
    updateLine(i, {
      description: preset.description,
      unit: preset.unit,
      unit_price: preset.unit_price,
      vat_percent: preset.vat_percent,
    });

  // Live total recompute (zelfde regels als backend per regel BTW).
  const totals = useMemo(() => {
    let sub = 0, vat = 0;
    lines.forEach((l) => {
      const qty = Number(l.quantity) || 0;
      const unit = Number(l.unit_price) || 0;
      const pct = Number(l.vat_percent) || 0;
      const excl = qty * unit;
      sub += excl;
      vat += (excl * pct) / 100;
    });
    return { sub, vat, total: sub + vat };
  }, [lines]);

  const totalBelowPaid = isProtected && totals.total < totalPaidExisting - 0.0001;

  const handleSubmit = async () => {
    if (!(form.customer_name.trim() || form.customer_company.trim())) {
      toast.error("Klantnaam of bedrijfsnaam is verplicht");
      return;
    }
    if (lines.length === 0 || lines.every((l) => !l.description.trim())) {
      toast.error("Voeg minimaal 1 factuurregel toe");
      return;
    }
    if (isProtected && !editReason.trim()) {
      toast.error("Reden is verplicht bij wijzigen van een (deels) betaalde factuur");
      return;
    }
    if (totalBelowPaid) {
      toast.error(`Nieuw totaal (${fmtEUR(totals.total, form.currency)}) is lager dan al betaald (${fmtEUR(totalPaidExisting, form.currency)}). Pas eerst betalingen aan of registreer een credit-correctie.`);
      return;
    }

    setBusy(true);
    try {
      const payload = {
        customer_name: form.customer_name.trim(),
        customer_company: form.customer_company.trim() || null,
        customer_email: (form.customer_email || "").toLowerCase().trim() || null,
        customer_phone: form.customer_phone.trim() || null,
        customer_whatsapp: form.customer_whatsapp.trim() || null,
        street: form.street.trim() || null,
        house_number: form.house_number.trim() || null,
        house_number_addition: form.house_number_addition.trim() || null,
        postal_code: form.postal_code.trim() || null,
        city: form.city.trim() || null,
        country: form.country.trim() || null,
        vat_number: form.vat_number.trim() || null,
        kvk_number: form.kvk_number.trim() || null,
        invoice_date: form.invoice_date || null,
        due_date: form.due_date || null,
        currency: form.currency || "EUR",
        payment_reference: form.payment_reference.trim() || null,
        customer_reference: form.customer_reference.trim() || null,
        service_type: form.service_type.trim() || null,
        destination: form.destination.trim() || null,
        customer_note: form.customer_note.trim() || null,
        internal_note: form.internal_note.trim() || null,
        lines: lines
          .filter((l) => l.description.trim())
          .map((l) => {
            // P1 Bugfix (Feb 2026): behoud quantity=0. Alleen NaN → 0.
            const qtyNum = Number(l.quantity);
            const qty = Number.isFinite(qtyNum) ? qtyNum : 0;
            const priceNum = Number(l.unit_price);
            const price = Number.isFinite(priceNum) ? priceNum : 0;
            const vatNum = Number(l.vat_percent);
            const vat = Number.isFinite(vatNum) ? vatNum : 0;
            return {
              description: l.description.trim(),
              quantity: qty,
              unit: l.unit || "stuk",
              unit_price: price,
              vat_percent: vat,
            };
          }),
      };
      if (isProtected) payload.edit_reason = editReason.trim();
      await adminApi.patch(`/admin/sales-invoices/${invoice.id}`, payload);
      toast.success(`Factuur ${invoice.invoice_number} bijgewerkt`);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bewerken mislukt");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="invoice-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-chivo font-black text-xl text-[#082c50] flex items-center gap-2">
            <Edit3 className="w-5 h-5" /> Factuur bewerken — {invoice.invoice_number}
          </DialogTitle>
          <DialogDescription>
            Factuurnummer en id blijven onveranderd. Bij een (deels) betaalde factuur is een reden verplicht
            en mag het nieuwe totaal niet onder het reeds betaalde bedrag komen.
          </DialogDescription>
        </DialogHeader>

        {isProtected && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900 flex items-start gap-2" data-testid="invoice-edit-protected-warning">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-bold uppercase tracking-wide text-[10px]">Beschermde factuur</div>
              Deze factuur heeft status <strong>{invoice.status === "betaald" ? "betaald" : "deels betaald"}</strong>.
              Reeds betaald: <span className="font-mono font-bold">{fmtEUR(totalPaidExisting, form.currency)}</span>.
              Bewaarbaar onder verplichte reden.
            </div>
          </div>
        )}

        {/* P1 4 Mrt 2026 — Klantkoppeling */}
        <InvoiceCustomerLinkPanel
          invoice={invoice}
          canManage={canManageLink}
        />

        {/* Klantgegevens */}
        <div className="bg-white border border-gray-200 p-3 sm:p-4">
          <h3 className="text-xs uppercase font-bold tracking-wide text-gray-700 mb-3">Klantgegevens</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div>
              <Label className="text-[10px] uppercase font-bold">Klantnaam *</Label>
              <Input value={form.customer_name} onChange={(e) => setF("customer_name", e.target.value)} className="rounded-xl mt-1" data-testid="invoice-edit-customer_name" />
            </div>
            <div>
              <Label className="text-[10px] uppercase font-bold">Bedrijfsnaam</Label>
              <Input value={form.customer_company} onChange={(e) => setF("customer_company", e.target.value)} className="rounded-xl mt-1" data-testid="invoice-edit-company" />
            </div>
            <div>
              <Label className="text-[10px] uppercase font-bold">E-mail</Label>
              <Input type="email" value={form.customer_email} onChange={(e) => setF("customer_email", e.target.value)} className="rounded-xl mt-1" data-testid="invoice-edit-email" />
            </div>
            <div>
              <Label className="text-[10px] uppercase font-bold">Telefoon</Label>
              <Input value={form.customer_phone} onChange={(e) => setF("customer_phone", e.target.value)} className="rounded-xl mt-1" data-testid="invoice-edit-phone" />
            </div>
            <div>
              <Label className="text-[10px] uppercase font-bold">WhatsApp</Label>
              <Input value={form.customer_whatsapp} onChange={(e) => setF("customer_whatsapp", e.target.value)} className="rounded-xl mt-1" data-testid="invoice-edit-whatsapp" />
            </div>
            <div>
              <Label className="text-[10px] uppercase font-bold">BTW-nummer</Label>
              <Input value={form.vat_number} onChange={(e) => setF("vat_number", e.target.value)} className="rounded-xl mt-1" data-testid="invoice-edit-vat" />
            </div>
            <div>
              <Label className="text-[10px] uppercase font-bold">KvK</Label>
              <Input value={form.kvk_number} onChange={(e) => setF("kvk_number", e.target.value)} className="rounded-xl mt-1" data-testid="invoice-edit-kvk" />
            </div>
          </div>
        </div>

        {/* Adres */}
        <div className="bg-white border border-gray-200 p-3 sm:p-4">
          <h3 className="text-xs uppercase font-bold tracking-wide text-gray-700 mb-3">Adresgegevens</h3>
          <div className="grid grid-cols-1 sm:grid-cols-6 gap-3 text-sm">
            <div className="sm:col-span-3">
              <Label className="text-[10px] uppercase font-bold">Straat</Label>
              <Input value={form.street} onChange={(e) => setF("street", e.target.value)} className="rounded-xl mt-1" data-testid="invoice-edit-street" />
            </div>
            <div className="sm:col-span-1">
              <Label className="text-[10px] uppercase font-bold">Huisnr.</Label>
              <Input value={form.house_number} onChange={(e) => setF("house_number", e.target.value)} className="rounded-xl mt-1" data-testid="invoice-edit-house_number" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[10px] uppercase font-bold">Toev.</Label>
              <Input value={form.house_number_addition} onChange={(e) => setF("house_number_addition", e.target.value)} className="rounded-xl mt-1" data-testid="invoice-edit-house_addition" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[10px] uppercase font-bold">Postcode</Label>
              <Input value={form.postal_code} onChange={(e) => setF("postal_code", e.target.value)} className="rounded-xl mt-1" data-testid="invoice-edit-postal" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[10px] uppercase font-bold">Plaats</Label>
              <Input value={form.city} onChange={(e) => setF("city", e.target.value)} className="rounded-xl mt-1" data-testid="invoice-edit-city" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[10px] uppercase font-bold">Land</Label>
              <Input value={form.country} onChange={(e) => setF("country", e.target.value)} className="rounded-xl mt-1" data-testid="invoice-edit-country" />
            </div>
          </div>
        </div>

        {/* Factuurgegevens */}
        <div className="bg-white border border-gray-200 p-3 sm:p-4">
          <h3 className="text-xs uppercase font-bold tracking-wide text-gray-700 mb-3">Factuurgegevens</h3>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 text-sm">
            <div>
              <Label className="text-[10px] uppercase font-bold">Factuurdatum</Label>
              <Input type="date" value={form.invoice_date} onChange={(e) => setF("invoice_date", e.target.value)} className="rounded-xl mt-1" data-testid="invoice-edit-invoice_date" />
            </div>
            <div>
              <Label className="text-[10px] uppercase font-bold">Vervaldatum</Label>
              <Input type="date" value={form.due_date} onChange={(e) => setF("due_date", e.target.value)} className="rounded-xl mt-1" data-testid="invoice-edit-due_date" />
            </div>
            <div>
              <Label className="text-[10px] uppercase font-bold">Valuta</Label>
              <Select value={form.currency} onValueChange={(v) => setF("currency", v)}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="invoice-edit-currency"><SelectValue /></SelectTrigger>
                <SelectContent>{(meta?.currencies || SUPPORTED_CURRENCIES).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase font-bold">Betalingsreferentie</Label>
              <Input value={form.payment_reference} onChange={(e) => setF("payment_reference", e.target.value)} className="rounded-xl mt-1" data-testid="invoice-edit-payment_reference" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[10px] uppercase font-bold">Klantreferentie</Label>
              <Input value={form.customer_reference} onChange={(e) => setF("customer_reference", e.target.value)} placeholder="bv. warehouse-nr / tracking" className="rounded-xl mt-1" data-testid="invoice-edit-customer_reference" />
            </div>
            <div>
              <Label className="text-[10px] uppercase font-bold">Service / type</Label>
              <Input value={form.service_type} onChange={(e) => setF("service_type", e.target.value)} className="rounded-xl mt-1" data-testid="invoice-edit-service_type" />
            </div>
            <div>
              <Label className="text-[10px] uppercase font-bold">Bestemming</Label>
              <Input value={form.destination} onChange={(e) => setF("destination", e.target.value)} className="rounded-xl mt-1" data-testid="invoice-edit-destination" />
            </div>
            <div className="sm:col-span-4">
              <Label className="text-[10px] uppercase font-bold">Notitie op factuur (zichtbaar voor klant)</Label>
              <Textarea rows={2} value={form.customer_note} onChange={(e) => setF("customer_note", e.target.value)} className="rounded-xl mt-1" data-testid="invoice-edit-customer_note" />
            </div>
            <div className="sm:col-span-4">
              <Label className="text-[10px] uppercase font-bold">Interne notitie (alleen backoffice)</Label>
              <Textarea rows={2} value={form.internal_note} onChange={(e) => setF("internal_note", e.target.value)} className="rounded-xl mt-1" data-testid="invoice-edit-internal_note" />
            </div>
          </div>
        </div>

        {/* Lines */}
        <div className="bg-white border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs uppercase font-bold tracking-wide text-gray-700">Factuurregels</h3>
            <Button size="sm" onClick={addLine} variant="outline" className="rounded-xl text-xs" data-testid="invoice-edit-add-line">
              <Plus className="w-3 h-3 mr-1" /> Regel toevoegen
            </Button>
          </div>
          <div className="space-y-2">
            {lines.map((l, i) => (
              <div className="border border-gray-200 p-2 rounded" data-testid={`invoice-edit-line-${i}`} key={i}>
                <div className="grid grid-cols-2 sm:flex sm:flex-wrap sm:items-end gap-2">
                  <div className="col-span-2 sm:flex-1 sm:min-w-[280px]">
                    <Label className="text-[10px] uppercase font-bold">Omschrijving</Label>
                    <Input value={l.description} onChange={(e) => updateLine(i, { description: e.target.value })} className="rounded-xl mt-1 h-9 text-sm" data-testid={`invoice-edit-line-${i}-description`} />
                  </div>
                  <div className="sm:w-20">
                    <Label className="text-[10px] uppercase font-bold">Aantal</Label>
                    <Input type="number" step="0.01" value={l.quantity} onChange={(e) => updateLine(i, { quantity: e.target.value })} className="rounded-xl mt-1 h-9 text-xs" data-testid={`invoice-edit-line-${i}-quantity`} />
                  </div>
                  <div className="sm:w-28">
                    <Label className="text-[10px] uppercase font-bold">Eenheid</Label>
                    <Select value={l.unit || "stuk"} onValueChange={(v) => updateLine(i, { unit: v })}>
                      <SelectTrigger className="rounded-xl mt-1 h-9 text-xs" data-testid={`invoice-edit-line-${i}-unit`}><SelectValue /></SelectTrigger>
                      <SelectContent>{(meta?.units || ["stuk"]).map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="sm:w-28">
                    <Label className="text-[10px] uppercase font-bold">Prijs / eenheid</Label>
                    <Input type="number" step="0.01" value={l.unit_price} onChange={(e) => updateLine(i, { unit_price: e.target.value })} className="rounded-xl mt-1 h-9 text-xs text-right font-mono" data-testid={`invoice-edit-line-${i}-unit_price`} />
                  </div>
                  <div className="sm:w-20">
                    <Label className="text-[10px] uppercase font-bold">BTW %</Label>
                    <Select value={String(l.vat_percent)} onValueChange={(v) => updateLine(i, { vat_percent: Number(v) })}>
                      <SelectTrigger className="rounded-xl mt-1 h-9 text-xs" data-testid={`invoice-edit-line-${i}-vat`}><SelectValue /></SelectTrigger>
                      <SelectContent>{(meta?.vat_options || [0, 9, 21]).map((v) => <SelectItem key={v} value={String(v)}>{v}%</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2 sm:w-24 flex items-end justify-between sm:justify-end sm:block pb-2">
                    <span className="sm:hidden text-[10px] uppercase font-bold text-gray-500">Regeltotaal</span>
                    <div className="text-sm sm:text-xs font-mono font-bold text-[#082c50]" data-testid={`invoice-edit-line-${i}-total`}>
                      {((Number(l.quantity) || 0) * (Number(l.unit_price) || 0)).toFixed(2).replace(".", ",")}
                    </div>
                  </div>
                  {lines.length > 1 && (
                    <Button size="sm" variant="ghost" onClick={() => removeLine(i)} className="h-9 px-2 text-red-600 sm:pb-2" data-testid={`invoice-edit-line-${i}-remove`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <Label className="text-[10px] uppercase text-gray-500">Preset:</Label>
                  <Select value="" onValueChange={(v) => {
                    const p = (meta?.line_presets || []).find((x) => x.label === v);
                    if (p) applyPreset(i, p);
                  }}>
                    <SelectTrigger className="rounded-xl h-7 text-[10px] flex-1 sm:w-64 sm:flex-none" data-testid={`invoice-edit-line-${i}-preset`}>
                      <SelectValue placeholder="Kies snelle preset..." />
                    </SelectTrigger>
                    <SelectContent>
                      {(meta?.line_presets || []).map((p) => (
                        <SelectItem key={p.label} value={p.label}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))}
          </div>

          {/* Totals preview */}
          <div className="mt-4 p-3 bg-gray-50 border border-gray-200 text-right space-y-1">
            <div className="text-xs">Subtotaal excl. BTW: <span className="font-mono ml-2">{fmtEUR(totals.sub, form.currency)}</span></div>
            <div className="text-xs">BTW: <span className="font-mono ml-2">{fmtEUR(totals.vat, form.currency)}</span></div>
            <div className="text-base font-bold text-[#082c50]" data-testid="invoice-edit-total">
              Totaal incl. BTW: <span className="font-mono ml-2">{fmtEUR(totals.total, form.currency)}</span>
            </div>
            {totalBelowPaid && (
              <div className="text-xs text-red-700 font-bold mt-2" data-testid="invoice-edit-total-warning">
                ⚠ Nieuw totaal is lager dan reeds betaald ({fmtEUR(totalPaidExisting, form.currency)}). De factuur kan zo niet opgeslagen worden.
              </div>
            )}
          </div>
        </div>

        {isProtected && (
          <div className="bg-white border border-amber-300 p-3">
            <Label className="text-[10px] uppercase font-bold text-amber-900">Reden wijziging (verplicht) *</Label>
            <Textarea
              rows={2}
              value={editReason}
              onChange={(e) => setEditReason(e.target.value)}
              placeholder="Bijv. klant naam stond verkeerd, BTW-nummer ontbrak, regel toegevoegd ter compensatie…"
              maxLength={500}
              className="rounded-xl mt-1 text-xs"
              data-testid="invoice-edit-reason"
            />
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy} className="rounded-xl text-xs" data-testid="invoice-edit-cancel">
            Annuleren
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={busy || totalBelowPaid}
            className="bg-[#082c50] text-white rounded-xl text-xs font-chivo font-bold uppercase"
            data-testid="invoice-edit-save"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
            {busy ? "Opslaan…" : "Wijzigingen opslaan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
